import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card.jsx";
import { Badge } from "./components/ui/badge.jsx";
import { Separator } from "./components/ui/separator.jsx";
import * as FiIcons from 'react-icons/fi';
const { 
  FiCheckCircle, 
  FiStar, 
  FiTrendingUp, 
  FiEdit, 
  FiArrowRight, 
  FiPhoneCall 
} = FiIcons;
import SafeIcon from './common/SafeIcon.jsx';

// Brand Colors
const navyColor = "#003366";
const aquaColor = "#2EC4B6";
const yellowColor = "#FFCB05";

const standingStillData = [
  { metric: "Missed Calls %", Traditional: 38, "AI-Enabled": 8 },
  { metric: "Conversion %", Traditional: 21, "AI-Enabled": 39 },
  { metric: "No-Shows %", Traditional: 32, "AI-Enabled": 2 },
  { metric: "Patient Retention %", Traditional: 68, "AI-Enabled": 86 },
];

function SectionHeading({ eyebrow, title, kicker }) {
  return (
    <div className="mb-6">
      {eyebrow && (
        <div className="text-xs uppercase tracking-widest font-semibold text-accent mb-2">
          {eyebrow}
        </div>
      )}
      <h2 className="text-2xl md:text-3xl font-extrabold text-primary">
        {title}
      </h2>
      {kicker && <p className="text-sm text-gray-600 mt-1">{kicker}</p>}
    </div>
  );
}

// Lightweight replacement for ECharts Bar Chart
function SimpleBarChart() {
  return (
    <div className="space-y-5 mt-2 mb-6">
      {standingStillData.map((item, idx) => (
        <div key={idx} className="space-y-1.5">
          <div className="text-sm font-semibold text-gray-800">{item.metric}</div>
          <div className="flex items-center gap-3">
            <div className="w-20 text-xs text-right text-gray-500">Traditional</div>
            <div className="flex-1 h-5 bg-gray-100 rounded-r-md overflow-hidden flex">
              <div className="h-full transition-all duration-1000" style={{ backgroundColor: navyColor, width: `${item.Traditional}%` }}></div>
            </div>
            <div className="w-8 text-xs font-medium text-gray-700">{item.Traditional}%</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-20 text-xs text-right font-bold" style={{ color: aquaColor }}>AI-Enabled</div>
            <div className="flex-1 h-5 bg-gray-100 rounded-r-md overflow-hidden flex">
              <div className="h-full transition-all duration-1000" style={{ backgroundColor: aquaColor, width: `${item["AI-Enabled"]}%` }}></div>
            </div>
            <div className="w-8 text-xs font-bold" style={{ color: aquaColor }}>{item["AI-Enabled"]}%</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Lightweight replacement for ECharts Line Chart
function SimpleLineChart({ isFooter = false }) {
  const textColor = isFooter ? "text-white/80" : "text-gray-500";
  const gridColor = isFooter ? "border-white/20" : "border-gray-300";
  const laggardColor = isFooter ? yellowColor : navyColor;
  
  return (
    <div className="relative w-full h-full flex flex-col pt-2">
      <div className="flex justify-center gap-6 text-xs mb-3 font-medium">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-0.5" style={{ backgroundColor: aquaColor }}></div>
          <span className={isFooter ? "text-white" : "text-gray-700"}>Adopter</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className={`w-4 h-0.5 border-t border-dashed`} style={{ borderColor: laggardColor }}></div>
          <span className={isFooter ? "text-white" : "text-gray-700"}>Laggard</span>
        </div>
      </div>
      
      <div className={`relative flex-1 border-l border-b ${gridColor} mt-2 mb-5 ml-8 mr-4`}>
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full overflow-visible" preserveAspectRatio="none">
          {/* Adopter Curve */}
          <polyline points="0,83.3 25,70.8 50,56.6 75,37.5 100,15" fill="none" stroke={aquaColor} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
          {/* Laggard Curve */}
          <polyline points="0,83.3 25,85 50,86.6 75,88.3 100,89.1" fill="none" stroke={laggardColor} strokeWidth="2" strokeDasharray="4 4" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
        {/* Y-axis labels */}
        <div className={`absolute -left-8 top-0 text-[10px] ${textColor}`}>High</div>
        <div className={`absolute -left-8 bottom-0 text-[10px] ${textColor}`}>Low</div>
      </div>
      {/* X-axis labels */}
      <div className={`flex justify-between ml-8 mr-4 text-[10px] ${textColor}`}>
        <span>Now</span>
        <span>Year 2</span>
        <span>Year 4</span>
      </div>
    </div>
  );
}

export default function EducationalOnePager() {
  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            background: `radial-gradient(1200px 600px at 80% -10%,${aquaColor} 0%,transparent 60%),radial-gradient(900px 400px at -10% 110%,${yellowColor} 0%,transparent 60%)`,
          }}
        />
        <div className="mx-auto max-w-6xl px-6 pt-14 pb-10 md:pt-20 md:pb-16">
          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-primary">
            The Cost of Staying the Same in Dentistry
          </h1>
          <p className="mt-4 text-base md:text-lg text-gray-600 max-w-2xl">
            Dentistry is evolving fast. Patients expect instant access and digital convenience. Staffing is tighter than ever. AI is no longer futuristic — it's today's unfair advantage!
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Badge className="bg-accent text-white border-accent">
              <SafeIcon icon={FiStar} className="mr-1 h-3.5 w-3.5" />
              Adaptability wins
            </Badge>
          </div>

          <div className="mt-8">
            <SectionHeading 
              title="The Age of AI in Dentistry" 
              kicker="Intelligent systems now handle booking, communication, and insurance verification." 
            />
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex justify-center mb-6">
                  <div className="relative w-full max-w-2xl aspect-video rounded-lg overflow-hidden shadow-lg">
                    <iframe 
                      width="100%" 
                      height="100%" 
                      src="https://www.youtube.com/embed/fUaDo84mNdo?autoplay=1&mute=1&loop=1&playlist=fUaDo84mNdo&controls=1" 
                      title="AI in Dentistry Demo Video"
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                </div>
                <div className="text-center text-gray-600 italic">
                  "It feels like we hired a front desk team that never sleeps." — Dr. Z. Kingsberg
                </div>
                <div className="mt-4 flex justify-start">
                  <Badge variant="secondary" className="bg-white text-primary border border-primary">
                    <SafeIcon icon={FiTrendingUp} className="mr-1 h-3.5 w-3.5" />
                    Evidence-based
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 bg-white/70 backdrop-blur rounded-2xl shadow p-4">
            <div className="text-xs font-semibold text-gray-600 mb-2">AI Adoption Momentum</div>
            <div className="h-48 w-full max-w-2xl mx-auto">
              <SimpleLineChart />
            </div>
          </div>
        </div>
      </section>

      {/* HIDDEN PRICE OF STANDING STILL */}
      <section className="mx-auto max-w-6xl px-6 py-10">
        <SectionHeading 
          title="The Hidden Price of Standing Still" 
          kicker="If you're still relying on voicemail and manual follow-ups, you're losing ground." 
        />
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-primary">Traditional vs. AI-Enabled</CardTitle>
            </CardHeader>
            <CardContent>
              <SimpleBarChart />
              <ul className="mt-4 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiCheckCircle} className="h-4 w-4 text-accent mt-0.5 shrink-0" /> 
                  <span>75% of new patients don't leave a voicemail — they call the next practice that answers first.</span>
                </li>
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiCheckCircle} className="h-4 w-4 text-accent mt-0.5 shrink-0" /> 
                  <span>Margins shrink from administrative inefficiency.</span>
                </li>
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiCheckCircle} className="h-4 w-4 text-accent mt-0.5 shrink-0" /> 
                  <span>Marketing spend rises without conversion gains.</span>
                </li>
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiCheckCircle} className="h-4 w-4 text-accent mt-0.5 shrink-0" /> 
                  <span>Patients expect instant scheduling — and will go elsewhere to get it.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-primary">Lessons from Dentistry's Past</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <div className="bg-accent/10 p-2 rounded-full">
                  <SafeIcon icon={FiEdit} className="h-4 w-4 text-accent" />
                </div>
                <p><strong>Digital X-rays (1990s):</strong> Early skepticism gave way to faster diagnostics and better patient experience.</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-accent/10 p-2 rounded-full">
                  <SafeIcon icon={FiEdit} className="h-4 w-4 text-accent" />
                </div>
                <p><strong>CAD/CAM Crowns (2000s):</strong> Once dismissed as unreliable, same-day restorations became a profit driver.</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-accent/10 p-2 rounded-full">
                  <SafeIcon icon={FiEdit} className="h-4 w-4 text-accent" />
                </div>
                <p><strong>Online Reviews & SEO (2010s):</strong> Many ignored them — until competitors dominated Google Maps.</p>
              </div>
              <div className="pt-2">
                <Separator />
                <p className="text-gray-600 mt-4 font-medium italic">Lesson: those who adapt early lead; those who wait play catch-up.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ADAPTABILITY STORIES */}
      <section className="mx-auto max-w-6xl px-6 py-10 text-center">
        <SectionHeading title="Adaptability Stories That Mirror AI Adoption" />
        <div className="grid md:grid-cols-3 gap-6 text-left">
          <Card className="border-0 shadow-md">
            <CardHeader><CardTitle>Henry Ford & Model T</CardTitle></CardHeader>
            <CardContent className="text-sm text-gray-600">Carriage makers scoffed until they were out of business. Early adopters built new industries.</CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardHeader><CardTitle>Farmers & Tractors</CardTitle></CardHeader>
            <CardContent className="text-sm text-gray-600">Tractors were mocked as toys. Within a generation, adopters scaled; others couldn't keep up.</CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardHeader><CardTitle>iPhone vs. BlackBerry</CardTitle></CardHeader>
            <CardContent className="text-sm text-gray-600">Leaders laughed at touchscreens. Five years later, the market flipped. Adapters dominated.</CardContent>
          </Card>
        </div>
        <p className="mt-12 font-bold text-2xl md:text-3xl leading-tight text-primary">
          AI in Dentistry is no different. Early adopters will define the next era.
        </p>
      </section>

      {/* CAL.COM EMBED SECTION */}
      <section className="relative px-6 py-10">
        <div className="mx-auto max-w-6xl">
          <Card className="border-0 shadow-lg overflow-hidden bg-white">
            <div className="text-white p-6 md:p-8" style={{ backgroundColor: navyColor }}>
              <h3 className="text-xl md:text-2xl font-bold">Ready to Explore?</h3>
              <p className="opacity-90 mt-1">
                Find a time that works for you to connect with Ben.
              </p>
            </div>
            <CardContent className="p-0">
              <div className="w-full" style={{ height: '700px', minHeight: '600px' }}>
                <iframe 
                  src="https://cal.com/engagedentai/connect-with-ben-via-the-three-dental-dymamos" 
                  title="Cal.com Scheduling"
                  width="100%" 
                  height="100%" 
                  frameBorder="0" 
                  className="w-full h-full"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{backgroundColor: navyColor}} className="text-white py-10 mt-10">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h4 className="text-lg md:text-xl font-semibold">The Real Cost of Waiting</h4>
              <p className="mt-2 text-white/80 italic">"It's not the smartest or strongest who survive, but the most adaptable." — Charles Darwin</p>
            </div>
            <div className="h-44 bg-white/10 rounded-xl p-4">
               <SimpleLineChart isFooter={true} />
            </div>
          </div>
          <p className="mt-8 text-sm text-white/60 pt-6 border-t border-white/10">
            © {new Date().getFullYear()} EngageDent AI — More patients. Less friction. Bigger bottom lines.
          </p>
        </div>
      </footer>
    </div>
  );
}